import { useState } from "react";
import { postContact } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import { CheckCircle2, Mail, MapPin, Phone } from "lucide-react";

const FAQ = [
  { q: "Siparişim ne zaman kargoya verilir?", a: "Hafta içi saat 15:00'e kadar verilen siparişler aynı gün, sonrası ertesi iş günü kargoya verilir. (Demo bilgi)" },
  { q: "Hediye paketi ücretli mi?", a: "Hayır. Tüm siparişlerde özel Shule hediye kutusu ve el yazısı not kartı ücretsizdir." },
  { q: "İade sürecim nasıl işler?", a: "14 gün içinde ürünü kullanılmamış halde iade edebilirsin. İade kargo kodunu e-posta ile gönderiyoruz." },
  { q: "Ürünlerin kaplaması kararır mı?", a: "14 ayar altın kaplama parçalarımız 1 yıl bakım garantisi kapsamındadır; parlatma ve bakım desteği ücretsizdir." },
  { q: "Mağazalarda deneme yapabilir miyim?", a: "Evet, tüm mağazalarımızda dilediğin parçayı deneyebilir, stil danışmanlarımızdan öneri alabilirsin." },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await postContact(form);
      toast.success(r.message);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Mesaj gönderilemedi, tekrar dene");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full min-h-[52px] rounded-2xl border border-shule-lineaccent bg-shule-base px-4 text-sm outline-none focus:border-shule-gold transition-colors";

  return (
    <>
      <Seo title="İletişim" description="Shule'ye ulaş: soruların, önerilerin ve iş birlikleri için buradayız." path="/iletisim" />
      <div className="container-shule py-14 lg:py-20">
        <Reveal className="max-w-xl">
          <p className="eyebrow">İletişim</p>
          <h1 className="mt-3 font-serif text-5xl font-light text-shule-coffee">Sana kulak <span className="italic text-shule-goldhover">veriyoruz</span></h1>
          <p className="mt-4 text-shule-mocha leading-relaxed">Soru, öneri ya da sadece merhaba demek için yaz; en geç bir iş günü içinde dönüş yapalım.</p>
        </Reveal>

        <div className="mt-12 grid lg:grid-cols-[360px_1fr] gap-10">
          <div className="space-y-4">
            {[
              { icon: Mail, title: "E-posta", value: "merhaba@shule.com.tr", testid: "contact-email" },
              { icon: Phone, title: "Telefon", value: "+90 212 000 00 00 (09:00 – 18:00)", testid: "contact-phone" },
              { icon: MapPin, title: "Merkez", value: "Nişantaşı, İstanbul (demo adres)", testid: "contact-address" },
            ].map((c) => (
              <Reveal key={c.title}>
                <div data-testid={c.testid} className="flex gap-4 items-start rounded-3xl border border-shule-line bg-shule-paper p-5">
                  <span className="w-11 h-11 rounded-full bg-shule-goldlight/50 border border-shule-gold/30 flex items-center justify-center shrink-0">
                    <c.icon className="w-5 h-5 text-shule-goldhover" />
                  </span>
                  <div>
                    <p className="text-sm font-bold">{c.title}</p>
                    <p className="text-sm text-shule-mocha mt-0.5">{c.value}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1} className="rounded-[2rem] border border-shule-line bg-shule-paper p-6 sm:p-10 shadow-[0_10px_30px_-10px_rgba(61,46,40,0.05)]">
            {done ? (
              <div data-testid="contact-success" className="text-center py-12">
                <CheckCircle2 className="w-12 h-12 mx-auto text-shule-gold" />
                <h2 className="mt-4 font-serif text-3xl">Mesajın bize ulaştı</h2>
                <p className="mt-3 text-shule-mocha">En kısa sürede dönüş yapacağız.</p>
              </div>
            ) : (
              <form data-testid="contact-form" onSubmit={submit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="ct-name" className="text-sm font-semibold block mb-2">Ad Soyad</label>
                    <input id="ct-name" data-testid="contact-name-input" required minLength={2} className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} />
                  </div>
                  <div>
                    <label htmlFor="ct-email" className="text-sm font-semibold block mb-2">E-posta</label>
                    <input id="ct-email" data-testid="contact-email-input" type="email" required className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} />
                  </div>
                </div>
                <div>
                  <label htmlFor="ct-subject" className="text-sm font-semibold block mb-2">Konu</label>
                  <input id="ct-subject" data-testid="contact-subject-input" required minLength={2} className={inputCls} value={form.subject} onChange={(e) => set("subject", e.target.value)} />
                </div>
                <div>
                  <label htmlFor="ct-message" className="text-sm font-semibold block mb-2">Mesajın</label>
                  <textarea id="ct-message" data-testid="contact-message-input" required minLength={10} rows={5} value={form.message} onChange={(e) => set("message", e.target.value)}
                    className="w-full rounded-2xl border border-shule-lineaccent bg-shule-base px-4 py-3 text-sm outline-none focus:border-shule-gold transition-colors resize-none" />
                </div>
                <button data-testid="contact-submit-button" disabled={loading} className="btn-primary-shule w-full disabled:opacity-40">
                  {loading ? "Gönderiliyor…" : "Mesajı Gönder"}
                </button>
              </form>
            )}
          </Reveal>
        </div>

        <Reveal className="mt-16 max-w-3xl" id="sss">
          <p className="eyebrow">Sık Sorulanlar</p>
          <h2 className="mt-2 font-serif text-3xl mb-6">Merak edilenler</h2>
          <Accordion type="single" collapsible>
            {FAQ.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`}>
                <AccordionTrigger data-testid={`faq-question-${i}`} className="text-sm font-semibold text-left min-h-[44px]">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-shule-mocha">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </>
  );
}
